import React, { useEffect, useState } from "react";
import { Wrapper, Container, Body, HeaderInfo } from "../../style/view.styles";

import {
  Section,
  SectionTitle,
  SectionContent,
  DashboardLayout,
  TopSection,
  DashboardCard,
  CardContent,
  BottomSection,
  ApplicationsTable,
  InterviewsContainer,
  InterviewCard,
} from "../../style/talent.dashboard.styles";
import LoadingSpinner from "../../components/Elements/LoadingSpinner";
import EmptyState from "../../components/Elements/EmptyState";
import { fetchEntityData } from "../../utils/RequestHandler";
import {
  getLoggedInOrganization,
  getLoggedInUser,
} from "../../utils/AuthHandler";

export default function OrganizationDashboard() {
  const [loading, setLoading] = useState(true);
  const [openRoles, setOpenRoles] = useState(0);
  const [activeInterviews, setActiveInterviews] = useState(0);
  const [newApplicants, setNewApplicants] = useState(0);
  const [teamProgress, setTeamProgress] = useState(0);
  const [recentApplicants, setRecentApplicants] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isProtected, setIsProtected] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const user = getLoggedInUser();
      const profile = await getLoggedInOrganization(user);
      if (user) {
        const role = user.role;
        setIsLoggedIn(true);
        setUserRole(role);
        setProfile(profile);
        setIsProtected(role === "organization");
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
        setIsProtected(false);
      }
    };
    checkUserLoggedIn();

    // TODO: Need to setup these values ASAP
    setActiveInterviews(0);
    setNewApplicants(0);
    setTeamProgress(0);
    setRecentApplicants([]);
    setUpcomingInterviews([]);
  }, []);

  useEffect(() => {
    if (isLoggedIn !== null && isProtected !== null) {
      const fetchOpportunities = async () => {
        try {
          setLoading(true);
          const data = await fetchEntityData("opportunities", isProtected);
          if (data.success) {
            const records = data.result;
            setOpenRoles(records.length);
          }
        } catch (error) {
          console.error("Failed to fetch opportunities:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchOpportunities();
    }
  }, [isProtected, isLoggedIn, userRole]);

  const getStatusClass = (status) => {
    switch (status) {
      case "applied":
        return "status-applied";
      case "interview":
        return "status-interview";
      case "shortlisted":
        return "status-shortlisted";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Wrapper className="paleBlueGreyBg">
      <Container>
        <Body style={{ width: "100%" }}>
          <HeaderInfo>
            <h1 className="font30 darkGreenColor extraBold">
              {profile && profile.name}
              {!profile && `Welcome,`}
            </h1>
          </HeaderInfo>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <DashboardLayout>
              <Section>
                <TopSection>
                  <DashboardCard>
                    <h3>Open Roles</h3>
                    <CardContent>
                      <div className="card-count">{openRoles}</div>
                      <div className="card-description">
                        Roles currently accepting applications
                      </div>
                    </CardContent>
                  </DashboardCard>

                  <DashboardCard>
                    <h3>Active Interviews</h3>
                    <CardContent>
                      <div className="card-count">{activeInterviews}</div>
                      <div className="card-description">
                        Interviews in progress
                      </div>
                    </CardContent>
                  </DashboardCard>

                  <DashboardCard>
                    <h3>New Applicants</h3>
                    <CardContent>
                      <div className="card-count">{newApplicants}</div>
                      <div className="card-description">
                        Applicants who applied this week
                      </div>
                    </CardContent>
                  </DashboardCard>

                  <DashboardCard>
                    <h3>Team Review Progress</h3>
                    <CardContent>
                      <div className="card-count">
                        {teamProgress > 0 ? `${teamProgress}%` : `--`}
                      </div>
                      <div className="card-description">
                        Candidate reviews completed by your team
                      </div>
                    </CardContent>
                  </DashboardCard>
                </TopSection>
              </Section>

              <BottomSection>
                <Section>
                  <SectionTitle>Recent Applicants</SectionTitle>
                  <SectionContent>
                    {recentApplicants.length > 0 ? (
                      <ApplicationsTable>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Position</th>
                            <th>Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentApplicants.map((app) => (
                            <tr key={app.id}>
                              <td>{app.name}</td>
                              <td>{app.position}</td>
                              <td>{formatDate(app.date)}</td>
                              <td>
                                <span
                                  className={`status ${getStatusClass(
                                    app.status
                                  )}`}
                                >
                                  {app.status.charAt(0).toUpperCase() +
                                    app.status.slice(1)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </ApplicationsTable>
                    ) : (
                      <EmptyState message="No recent applicants." />
                    )}
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>Upcoming Interviews</SectionTitle>
                  <SectionContent>
                    {upcomingInterviews.length > 0 ? (
                      <InterviewsContainer>
                        {upcomingInterviews.map((interview) => (
                          <InterviewCard key={interview.id}>
                            <div className="interview-date">
                              {formatDate(interview.date)}
                            </div>
                            <div className="interview-title">
                              {interview.position}
                            </div>
                            <div className="interview-company">
                              {interview.candidate}
                            </div>
                            <div className="interview-time">
                              {interview.time} • {interview.type}
                            </div>
                          </InterviewCard>
                        ))}
                      </InterviewsContainer>
                    ) : (
                      <EmptyState message="No upcoming interviews scheduled." />
                    )}
                  </SectionContent>
                </Section>
              </BottomSection>
            </DashboardLayout>
          )}
        </Body>
      </Container>
    </Wrapper>
  );
}
